import React, { useEffect, useRef, useState } from "react";
import { useConfirm } from "@/hooks/useConfirm";
import ConfirmDialog from "@/components/ConfirmDialog";
import { Send, Hash, AtSign, Code2, Paperclip, X, Image as ImageIcon, File as FileIcon, Smile, Bug, Video, Terminal, Bot, Loader2, Lock, Sparkles, Cloud, Mic, MicOff } from "lucide-react";
import { api, uploadFile, streamOpsClaude } from "@/lib/api";
import Message from "@/components/Message";
import ThreadPanel from "@/components/ThreadPanel";
import EmojiPicker from "@/components/EmojiPicker";
import FeedbackModal from "@/components/FeedbackModal";
import Avatar from "@/components/Avatar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ring, stopRing } from "@/utils/audio";
import useDictation from "@/hooks/useDictation";
import { toast } from "sonner";
import { ROLES, roleLabel } from "@/lib/labels";

export default function ChatView({ mode, target, workspaceId, currentUser, users, isOwner = false, myRole = "member", opsConfig = null, subscribe, onOpenFeedback, onStartCall, onJoinCall, assistantOpen = false, assistantName = "Atena", onToggleAssistant }) {
  const { ask, confirmProps } = useConfirm();
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [pendingFiles, setPendingFiles] = useState([]);  // file records returned from upload
  // Microfonul era doar în panoul Atena. Aici e locul unde omul chiar vorbește:
  // în DM și în canal. Același hook, aceeași rută /api/dictate — transcriere pe
  // serverul nostru, deci merge și în Firefox și în WebView-ul Android, unde
  // SpeechRecognition nu există.
  const dictation = useDictation({
    workspaceId,
    onText: (said) => setText((prev) => (prev ? `${prev} ${said}` : said)),
  });
  const [uploading, setUploading] = useState(false);
  const [threadParent, setThreadParent] = useState(null);
  const [mentionMenu, setMentionMenu] = useState({ open: false, query: "" });
  const [emojiOpen, setEmojiOpen] = useState(false);
  const [feedback, setFeedback] = useState(null);  // { title } when the modal is open
  const [typingUsers, setTypingUsers] = useState({});  // user_id -> { name, ts }
  const [activeCall, setActiveCall] = useState(null);  // { room_name, started_by } when someone started a call here
  const [runMenuOpen, setRunMenuOpen] = useState(false);
  const [runningCommand, setRunningCommand] = useState("");
  const [claudeOpen, setClaudeOpen] = useState(false);
  const [claudePrompt, setClaudePrompt] = useState("");
  const [claudeBusy, setClaudeBusy] = useState(false);
  const [claudeStream, setClaudeStream] = useState("");
  const [ccServer, setCcServer] = useState(null);       // "contabo" | "hetzner" | null
  const [ccPrompt, setCcPrompt] = useState("");
  const [ccBusy, setCcBusy] = useState(false);
  const [ccResult, setCcResult] = useState("");
  const [ccConfirm, setCcConfirm] = useState(null);     // { actions } awaiting confirmation
  const [accessOpen, setAccessOpen] = useState(false);
  const [accessRoles, setAccessRoles] = useState([]);
  const [accessSaving, setAccessSaving] = useState(false);
  const [kmeetBusy, setKmeetBusy] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const lastTypingRef = useRef(0);
  const typingActiveRef = useRef(false);

  const targetId = target?.id;
  const canManageChannel = mode === "channel" && (myRole === "owner" || myRole === "admin");
  const isOpsChannel = mode === "channel" && isOwner && targetId === opsConfig?.channel_id;
  const opsCommands = opsConfig?.commands || [];

  useEffect(() => {
    if (!targetId || !workspaceId) return;
    const params = { workspace_id: workspaceId };
    if (mode === "channel") params.channel_id = targetId;
    else params.recipient_id = targetId;
    api.get("/messages", { params }).then((r) => setMessages(r.data));
  }, [targetId, mode, workspaceId]);

  useEffect(() => {
    if (!subscribe) return;
    const unsub = subscribe((ev) => {
      // Typing events carry their own targeting shape (handle before the message gate).
      if (ev.type === "typing") {
        const d = ev.data || {};
        const forThis = mode === "channel"
          ? d.channel_id === targetId
          : (d.channel_id == null && d.user_id === targetId && d.dm_recipient_id === currentUser.id);
        if (forThis) setTypingUsers((prev) => {
          if (d.is_typing === false) {
            if (!prev[d.user_id]) return prev;
            const next = { ...prev }; delete next[d.user_id]; return next;
          }
          return { ...prev, [d.user_id]: { name: d.user_name, ts: Date.now() } };
        });
        return;
      }
      // Call started in this conversation — show a Join banner (not for the starter).
      if (ev.type === "call_started") {
        const forThisConversation = mode === "channel"
          ? ev.channel_id === targetId
          : ev.dm_participants?.includes(currentUser.id) && ev.dm_participants?.includes(targetId);
        if (forThisConversation && ev.started_by?.id !== currentUser.id) {
          setActiveCall({ room_name: ev.room_name, started_by: ev.started_by });
          ring().catch(() => { /* autoplay blocked */ });
        }
        return;
      }
      const m = ev.data;
      if (!m) return;
      const isForThis =
        mode === "channel"
          ? m.channel_id === targetId && !m.parent_id
          : (m.sender_id === currentUser.id && m.recipient_id === targetId)
            || (m.sender_id === targetId && m.recipient_id === currentUser.id);
      if (!isForThis) return;
      if (ev.type === "message") {
        setMessages((prev) => prev.find((x) => x.id === m.id) ? prev : [...prev, m]);
        setTypingUsers((prev) => {  // sender clearly stopped typing
          if (!prev[m.sender_id]) return prev;
          const next = { ...prev }; delete next[m.sender_id]; return next;
        });
      } else if (ev.type === "message_update") {
        setMessages((prev) => prev.map((x) => x.id === m.id ? m : x));
      } else if (ev.type === "message_delete") {
        setMessages((prev) => prev.filter((x) => x.id !== m.id));
      }
    });
    return unsub;
  }, [subscribe, mode, targetId, currentUser.id]);

  // Reset transient typing/call state when switching conversation.
  useEffect(() => {
    setTypingUsers({}); setActiveCall(null);
    return () => { stopRing(); };
  }, [targetId, mode]);

  // Expire stale typing indicators (peers stop sending after they pause).
  useEffect(() => {
    const t = setInterval(() => {
      setTypingUsers((prev) => {
        const now = Date.now();
        const next = {};
        let changed = false;
        for (const [uid, v] of Object.entries(prev)) {
          if (now - v.ts < 5000) next[uid] = v; else changed = true;
        }
        return changed ? next : prev;
      });
    }, 1500);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const onPick = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    setUploading(true);
    try {
      for (const f of files) {
        const rec = await uploadFile(workspaceId, f);
        setPendingFiles((prev) => [...prev, rec]);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Încărcarea nu a reușit");
    } finally { setUploading(false); }
  };

  const runOpsCommand = async (command) => {
    if (!isOpsChannel || !opsCommands.includes(command) || runningCommand) return;
    setRunMenuOpen(false);
    setRunningCommand(command);
    try {
      await api.post("/ops/run", { command, channel_id: targetId });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Comanda nu a reușit");
    } finally {
      setRunningCommand("");
    }
  };

  const runClaudeCode = async (confirmDestructive = false) => {
    const prompt = ccPrompt.trim();
    if (!isOpsChannel || !ccServer || !prompt || ccBusy) return;
    setCcBusy(true);
    setCcResult("");
    setCcConfirm(null);
    try {
      const { data } = await api.post("/ops/claude-code",
        { server: ccServer, prompt, confirm_destructive: confirmDestructive },
        { params: { channel_id: targetId } });
      setCcResult(data.output || "");
      setCcPrompt("");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      // FastAPI wraps the dict detail; a 400 with requires_confirmation opens the modal.
      if (detail && typeof detail === "object" && detail.requires_confirmation) {
        setCcConfirm({ actions: detail.destructive_actions || [] });
      } else {
        toast.error(typeof detail === "string" ? detail : "Claude Code a eșuat");
      }
    } finally { setCcBusy(false); }
  };

  const sendClaudePrompt = async (rawPrompt = claudePrompt) => {
    const prompt = rawPrompt.trim();
    if (!isOpsChannel || !prompt || claudeBusy) return;
    setClaudeBusy(true);
    setClaudeStream("");
    try {
      await streamOpsClaude({
        prompt,
        channelId: targetId,
        onDelta: (delta) => setClaudeStream((current) => current + delta),
        onError: (message) => {
          toast.error(message || "Cererea către Claude nu a reușit");
          setClaudeBusy(false);
        },
        onDone: () => {
          setClaudeBusy(false);
          setClaudeStream("");
        },
      });
      setClaudePrompt("");
      setClaudeOpen(false);
    } finally {
      setClaudeBusy(false);
    }
  };

  const requestStartCall = (customRoom = null) => {
    if (!target?.id) return;
    const destination = mode === "channel" ? `#${target.name}` : target.name;
    ask({
      title: `Pornești un apel în ${destination}?`, confirmLabel: "Pornește",
      description: "Ceilalți din conversație văd apelul și pot intra.",
      testId: "confirm-start-call",
      onConfirm: async () => {
        ring({ subtle: true }).catch(() => { /* autoplay blocked */ });
        try { await onStartCall?.(target, customRoom, mode); }
        finally { stopRing(); }
      },
    });
  };

  const send = async (e) => {
    e?.preventDefault();
    // /bug [title] — open the feedback modal prefilled instead of sending.
    const trimmed = text.trim();
    if (isOpsChannel && trimmed.toLowerCase() === "/run") {
      setRunMenuOpen(true);
      setText("");
      return;
    }
    if (isOpsChannel && trimmed.toLowerCase().startsWith("/run ")) {
      const command = trimmed.slice(5).trim().toLowerCase();
      if (opsCommands.includes(command)) {
        setText("");
        await runOpsCommand(command);
      } else {
        setRunMenuOpen(true);
        toast.error("Comanda nu e în lista permisă");
      }
      return;
    }
    if (isOpsChannel && trimmed.toLowerCase().startsWith("/claude")) {
      const prompt = trimmed.slice(7).trim();
      setText("");
      if (prompt) await sendClaudePrompt(prompt);
      else setClaudeOpen(true);
      return;
    }
    if (trimmed.toLowerCase().startsWith("/bug")) {
      setFeedback({ title: trimmed.slice(4).trim() });
      setText("");
      return;
    }
    // /call [custom-room] — start a Jitsi call in a channel or direct conversation.
    if (trimmed.toLowerCase().startsWith("/call")) {
      requestStartCall(trimmed.slice(5).trim() || null);
      setText("");
      return;
    }
    if ((!text.trim() && pendingFiles.length === 0) || !targetId) return;
    setSending(true);
    try {
      const body = {
        workspace_id: workspaceId,
        content: text,
        attachments: pendingFiles.map((f) => f.id),
      };
      if (mode === "channel") body.channel_id = targetId;
      else body.recipient_id = targetId;
      await api.post("/messages", body);
      setText(""); setPendingFiles([]);
      emitTyping(false);
    } catch {
      toast.error("Trimiterea nu a reușit");
    } finally { setSending(false); }
  };

  const onChange = (e) => {
    const v = e.target.value;
    setText(v);
    if (v.trim()) emitTyping(true);
    // Detect @ mention typing
    const before = v.slice(0, e.target.selectionStart || v.length);
    const m = before.match(/@([\w-]*)$/);
    if (m) setMentionMenu({ open: true, query: m[1] });
    else setMentionMenu({ open: false, query: "" });
  };

  const pickMention = (name) => {
    setText((t) => t.replace(/@([\w-]*)$/, `@${name} `));
    setMentionMenu({ open: false, query: "" });
    inputRef.current?.focus();
  };

  const insertCodeFence = () => {
    const el = inputRef.current;
    const start = el?.selectionStart ?? text.length;
    const end = el?.selectionEnd ?? text.length;
    const selected = text.slice(start, end);
    const fenced = selected ? `\`\`\`js\n${selected}\n\`\`\`` : "```js\n// code\n```";
    const next = text.slice(0, start) + fenced + text.slice(end);
    setText(next);
    requestAnimationFrame(() => {
      if (!el) return;
      const pos = start + fenced.length;
      el.focus();
      el.setSelectionRange(pos, pos);
    });
  };

  const insertEmoji = (emoji) => {
    const el = inputRef.current;
    const start = el?.selectionStart ?? text.length;
    const end = el?.selectionEnd ?? text.length;
    setText((t) => t.slice(0, start) + emoji + t.slice(end));
    setEmojiOpen(false);
    requestAnimationFrame(() => {
      if (!el) return;
      const pos = start + emoji.length;
      el.focus();
      el.setSelectionRange(pos, pos);
    });
  };

  const react = async (msgId, emoji) => {
    try { await api.post(`/messages/${msgId}/react`, { emoji }); }
    catch { toast.error("Reacția nu a reușit"); }
  };

  // Edits/deletes reflect into the list via the WS broadcast (author is a target too).
  const editMsg = async (msgId, content) => {
    try { await api.put(`/messages/${msgId}`, { content }); }
    catch (e) { toast.error(e?.response?.data?.detail || "Editarea nu a reușit"); throw e; }
  };

  const deleteMsg = async (msgId) => {
    try { await api.delete(`/messages/${msgId}`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Ștergerea nu a reușit"); throw e; }
  };

  const startKmeet = async () => {
    if (kmeetBusy) return;
    setKmeetBusy(true);
    try {
      // The room announces itself in the channel; the card arrives over WS.
      const { data } = await api.post("/kmeet/rooms",
        { channel_id: mode === "channel" ? targetId : null, subject: target?.name },
        { params: { workspace_id: workspaceId } });
      window.open(data.room_url, "_blank", "noopener,noreferrer");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "kMeet nu a putut porni");
    } finally { setKmeetBusy(false); }
  };

  const openAccess = () => {
    setAccessRoles(target?.allowed_roles || ["owner", "admin", "member"]);
    setAccessOpen(true);
  };

  const saveAccess = async () => {
    setAccessSaving(true);
    try {
      await api.patch(`/channels/${targetId}`, { allowed_roles: accessRoles });
      toast.success("Acces actualizat");
      setAccessOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Nu s-a putut salva");
    } finally { setAccessSaving(false); }
  };

  const emitTyping = (isTyping) => {
    if (isTyping) {
      const now = Date.now();
      if (now - lastTypingRef.current < 2500) return;  // throttle: at most once per 2.5s
      lastTypingRef.current = now;
    } else {
      if (!typingActiveRef.current) return;  // never started, nothing to clear
      lastTypingRef.current = 0;             // a stop is never throttled
    }
    typingActiveRef.current = isTyping;
    const payload = { workspace_id: workspaceId, is_typing: isTyping };
    if (mode === "channel") payload.channel_id = targetId; else payload.recipient_id = targetId;
    api.post("/messages/typing", payload).catch(() => { /* best-effort */ });
  };

  if (!target) {
    return (
      <div className="h-full flex items-center justify-center text-fg/40 text-sm">
        Alege un canal sau un mesaj direct ca să începi.
      </div>
    );
  }

  const mentionFiltered = users.filter((u) =>
    !mentionMenu.query || u.name.toLowerCase().includes(mentionMenu.query.toLowerCase())
  ).slice(0, 6);
  const runQuery = text.trimStart().toLowerCase().startsWith("/run")
    ? text.trimStart().slice(4).trim().toLowerCase()
    : null;
  const runSuggestions = runQuery === null
    ? []
    : opsCommands.filter((command) => !runQuery || command.includes(runQuery));

  const typingNames = Object.values(typingUsers).map((v) => v.name).filter(Boolean);
  const typingLabel =
    typingNames.length === 0 ? "" :
    typingNames.length === 1 ? `${typingNames[0]} scrie` :
    typingNames.length === 2 ? `${typingNames[0]} și ${typingNames[1]} scriu` :
    `${typingNames[0]}, ${typingNames[1]} și alți ${typingNames.length - 2} scriu`;

  return (
    <div className="h-full flex min-h-0 relative">
      <div className={`flex-1 flex flex-col min-h-0 min-w-0 ${threadParent ? "2xl:border-r 2xl:border-line/10" : ""}`}>
        {/* Header */}
        <div className="min-h-14 h-auto px-3 pl-12 sm:px-6 py-2 flex flex-wrap items-center gap-2 sm:gap-3 border-b border-line/10 bg-bg">
          {mode === "channel" ? (
            <>
              <Hash className="w-4 h-4 text-fg/50 flex-shrink-0" />
              <h2 className="font-bold tracking-tight truncate min-w-0">{target.name}</h2>
              <span className="hidden sm:inline text-fg/40 text-xs ml-2 truncate">{target.description}</span>
            </>
          ) : (
            <>
              <AtSign className="w-4 h-4 text-fg/50 flex-shrink-0" />
              <Avatar user={target} size={24} />
              <h2 className="font-bold tracking-tight truncate min-w-0">{target.name}</h2>
              <span className="hidden sm:inline text-fg/40 text-xs mono truncate">{target.email}</span>
            </>
          )}
          <div className="ml-auto flex flex-wrap items-center justify-end gap-2">
            {activeCall && (
              <>
                <button
                  data-testid="join-call-banner"
                  onClick={() => { stopRing(); onJoinCall?.(activeCall.room_name, target.name); }}
                  className="flex items-center gap-1.5 text-xs px-2.5 py-1 bg-brand/15 border border-brand/40 text-brand hover:bg-brand/25 transition-colors whitespace-nowrap"
                >
                  <Video className="w-3.5 h-3.5" /> 📹 Apel activ — Intră
                </button>
                <button
                  type="button"
                  data-testid="decline-call-banner"
                  aria-label="Respinge apelul"
                  onClick={() => { stopRing(); setActiveCall(null); }}
                  className="flex items-center gap-1.5 text-xs px-2.5 py-1 border border-line/20 text-fg/60 hover:text-fg hover:border-line/40 transition-colors whitespace-nowrap"
                >
                  <X className="w-3.5 h-3.5" /> Respinge
                </button>
              </>
            )}
            {onToggleAssistant && (
              <button
                type="button"
                data-testid="toggle-assistant-button"
                onClick={onToggleAssistant}
                aria-pressed={assistantOpen}
                title={`${assistantOpen ? "Închide" : "Deschide"} ${assistantName}`}
                aria-label={`${assistantOpen ? "Închide" : "Deschide"} panoul ${assistantName}`}
                className={`flex items-center gap-1.5 px-2.5 py-1 text-xs border transition-colors whitespace-nowrap ${
                  assistantOpen
                    ? "border-brand/40 text-brand bg-brand/10"
                    : "border-line/20 text-fg/60 hover:text-fg hover:border-line/40"}`}
              >
                <Sparkles className="w-3.5 h-3.5" /> {assistantName}
              </button>
            )}
            {canManageChannel && (
              <button
                type="button"
                data-testid="channel-access-trigger"
                onClick={openAccess}
                title="Acces canal"
                aria-label={`Setări de acces pentru #${target.name}`}
                className="flex items-center gap-1.5 px-2.5 py-1 text-xs border border-line/20 text-fg/60 hover:text-fg hover:border-line/40 transition-colors whitespace-nowrap"
              >
                <Lock className="w-3.5 h-3.5" /> Acces
              </button>
            )}
            {mode === "channel" && (
              <button
                type="button"
                data-testid="start-kmeet-button"
                onClick={startKmeet}
                disabled={kmeetBusy}
                title="Pornește o conferință kMeet (se deschide într-un tab nou)"
                aria-label={`Pornește kMeet în #${target.name}`}
                className="flex items-center gap-1.5 px-2.5 py-1 text-xs border border-line/20 text-fg/60 hover:text-fg hover:border-line/40 transition-colors whitespace-nowrap disabled:opacity-50"
              >
                {kmeetBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Cloud className="w-3.5 h-3.5" />}
                kMeet
              </button>
            )}
            <button
              data-testid="start-call-button"
              type="button"
              onClick={() => requestStartCall(null)}
              title="Pornește un apel video"
              aria-label={`Pornește un apel video în ${mode === "channel" ? "#" + target.name : target.name}`}
              className="flex items-center gap-1.5 px-2.5 py-1 text-xs border border-brand/40 text-brand bg-brand/10 hover:bg-brand/20 transition-colors whitespace-nowrap"
            >
              <Video className="w-3.5 h-3.5" /> Pornește un apel
            </button>
          </div>
        </div>

        {/* Messages */}
        <div ref={scrollRef} className="flex-1 overflow-y-auto px-3 sm:px-6 py-4 space-y-3" data-testid="messages-scroll">
          {messages.length === 0 && (
            <div className="text-center text-fg/40 text-sm py-12">
              <p className="label-tiny mb-2">// NICIUN MESAJ ÎNCĂ</p>
              <p>Salută, ca să înceapă conversația.</p>
            </div>
          )}
          {messages.map((m, i) => {
            const prev = messages[i - 1];
            const compact = prev && prev.sender_id === m.sender_id && (new Date(m.created_at) - new Date(prev.created_at)) < 5 * 60 * 1000;
            return (
              <Message
                key={m.id} msg={m} compact={compact}
                currentUserId={currentUser.id}
                onReact={(emoji) => react(m.id, emoji)}
                onOpenThread={() => setThreadParent(m)}
                onEdit={(content) => editMsg(m.id, content)}
                onDelete={() => deleteMsg(m.id)}
                onOpenFeedback={onOpenFeedback}
                onJoinCall={onJoinCall}
              />
            );
          })}
        </div>

        {/* Composer */}
        <form onSubmit={send} className="border-t border-line/10 bg-bg p-2 sm:p-3">
          {isOpsChannel && ccServer && (
            <div className="mb-2 border border-brand/30 bg-surface-2 p-2" data-testid="ops-claude-code-input">
              <div className="flex items-center gap-2 mb-2">
                {ccServer === "hetzner" ? <Cloud className="w-3.5 h-3.5 text-brand" /> : <Terminal className="w-3.5 h-3.5 text-brand" />}
                <span className="label-tiny">Claude Code // {ccServer.toUpperCase()}</span>
                <button type="button" onClick={() => { setCcServer(null); setCcResult(""); setCcConfirm(null); }} className="ml-auto text-fg/40 hover:text-fg">
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
              <div className="flex gap-2">
                <textarea
                  data-testid="ops-cc-prompt"
                  value={ccPrompt}
                  onChange={(e) => setCcPrompt(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); runClaudeCode(); } }}
                  rows={2}
                  placeholder={`Descrie ce vrei să facă Claude Code pe ${ccServer}...`}
                  className="flex-1 bg-bg border border-line/10 text-sm p-2 outline-none resize-none placeholder-fg/30"
                />
                <button
                  type="button" data-testid="ops-cc-send" onClick={() => runClaudeCode()}
                  disabled={ccBusy || !ccPrompt.trim()}
                  className="btn-primary text-sm px-3 disabled:opacity-40 self-stretch"
                >{ccBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Rulează"}</button>
              </div>
              {ccResult && (
                <pre data-testid="ops-cc-output" className="mt-2 max-h-64 overflow-auto bg-bg border border-line/10 p-2 text-[11px] mono whitespace-pre-wrap">{ccResult}</pre>
              )}
            </div>
          )}
          {isOpsChannel && ccConfirm && (
            <div className="mb-2 border border-destructive/40 bg-destructive/10 p-3" data-testid="ops-cc-confirm">
              <p className="text-sm font-medium mb-1">Acțiune distructivă detectată</p>
              <ul className="text-xs mono text-destructive mb-2 list-disc list-inside">
                {ccConfirm.actions.map((a) => <li key={a}>{a}</li>)}
              </ul>
              <label className="flex items-center gap-2 text-xs mb-2">
                <input type="checkbox" data-testid="ops-cc-ack"
                       onChange={(e) => setCcConfirm({ ...ccConfirm, ack: e.target.checked })} />
                Înțeleg, confirmă acțiunea
              </label>
              <div className="flex gap-2">
                <button type="button" data-testid="ops-cc-execute" disabled={!ccConfirm.ack || ccBusy}
                        onClick={() => runClaudeCode(true)}
                        className="text-xs px-3 py-1.5 bg-destructive text-destructive-foreground disabled:opacity-40">Execută</button>
                <button type="button" onClick={() => setCcConfirm(null)} className="btn-ghost text-xs px-3">Anulează</button>
              </div>
            </div>
          )}
          {isOpsChannel && claudeOpen && (
            <div className="mb-2 border border-brand/30 bg-surface-2 p-2" data-testid="ops-claude-input">
              <div className="flex items-center gap-2 mb-2">
                <Bot className="w-3.5 h-3.5 text-brand" />
                <span className="label-tiny">Claude DevOps</span>
                <button type="button" onClick={() => setClaudeOpen(false)} className="ml-auto text-fg/40 hover:text-fg">
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
              <div className="flex gap-2">
                <input
                  value={claudePrompt}
                  onChange={(e) => setClaudePrompt(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      sendClaudePrompt();
                    }
                  }}
                  autoFocus
                  placeholder="Întreabă Claude despre operațiuni…"
                  className="input-flat text-sm flex-1"
                />
                <button
                  type="button"
                  onClick={() => sendClaudePrompt()}
                  disabled={claudeBusy || !claudePrompt.trim()}
                  className="btn-primary text-xs px-3 disabled:opacity-40"
                >
                  {claudeBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Trimite"}
                </button>
              </div>
            </div>
          )}
          {isOpsChannel && claudeStream && (
            <div className="mb-2 border border-brand/20 bg-surface-2 p-3 text-sm text-fg/80 whitespace-pre-wrap" data-testid="ops-claude-stream">
              <span className="label-tiny block mb-1">🤖 Claude DevOps · streaming</span>
              {claudeStream}
            </div>
          )}
          {pendingFiles.length > 0 && (
            <div className="mb-2 flex flex-wrap gap-2">
              {pendingFiles.map((f, i) => (
                <div key={f.id} className="bg-surface-2 border border-line/10 px-2 py-1.5 flex items-center gap-2 text-xs">
                  {f.content_type?.startsWith("image/") ? <ImageIcon className="w-3.5 h-3.5 text-brand" /> : <FileIcon className="w-3.5 h-3.5 text-fg/50" />}
                  <span className="mono truncate max-w-[160px] sm:max-w-[200px]">{f.name}</span>
                  <button
                    type="button"
                    aria-label={`Elimină atașamentul ${f.name}`}
                    data-testid={`remove-attachment-${i}`}
                    onClick={() => setPendingFiles((prev) => prev.filter((x) => x.id !== f.id))}
                    className="text-fg/40 hover:text-red-400"
                  ><X className="w-3 h-3" /></button>
                </div>
              ))}
            </div>
          )}
          {typingLabel && (
            <div data-testid="typing-indicator" className="mb-1.5 flex items-center gap-1.5 text-[11px] text-fg-muted mono">
              <span className="flex gap-0.5">
                <span className="w-1 h-1 bg-fg-muted rounded-full pulse-amber" />
                <span className="w-1 h-1 bg-fg-muted rounded-full pulse-amber" style={{ animationDelay: "0.2s" }} />
                <span className="w-1 h-1 bg-fg-muted rounded-full pulse-amber" style={{ animationDelay: "0.4s" }} />
              </span>
              {typingLabel}…
            </div>
          )}
          <div className="bg-surface-2 border border-line/10 focus-within:border-line/30 transition-colors relative">
            {isOpsChannel && runSuggestions.length > 0 && (
              <div className="absolute bottom-full left-0 right-0 mb-1 bg-surface border border-line/10 max-h-52 overflow-y-auto z-20" data-testid="ops-run-autocomplete">
                <p className="label-tiny px-3 pt-2 pb-1">// COMENZI OPS PERMISE</p>
                {runSuggestions.map((command) => (
                  <button
                    key={command}
                    type="button"
                    onClick={() => { setText(""); runOpsCommand(command); }}
                    className="w-full text-left px-3 py-1.5 text-xs mono hover:bg-fg/5"
                  >
                    /run {command}
                  </button>
                ))}
              </div>
            )}
            {mentionMenu.open && mentionFiltered.length > 0 && (
              <div className="absolute bottom-full left-0 right-0 mb-1 bg-surface border border-line/10 max-h-48 overflow-y-auto z-10">
                <p className="label-tiny px-3 pt-2 pb-1">// MENȚIUNE</p>
                {mentionFiltered.map((u) => (
                  <button
                    key={u.id} type="button"
                    data-testid={`mention-option-${u.name}`}
                    onClick={() => pickMention(u.name)}
                    className="w-full text-left flex items-center gap-2 px-3 py-1.5 hover:bg-fg/5 text-sm"
                  >
                    <Avatar user={u} size={20} />
                    <span>{u.name}</span>
                    <span className="text-[10px] text-fg/40 mono">{u.email}</span>
                  </button>
                ))}
              </div>
            )}
            <textarea
              ref={inputRef}
              data-testid="message-input"
              value={text} onChange={onChange}
              onBlur={() => emitTyping(false)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder={`Scrie în ${mode === "channel" ? "#" + target.name : target.name} (folosește @ pentru mențiuni)`}
              rows={2}
              className="w-full bg-transparent text-fg text-sm p-3 outline-none resize-none placeholder-fg/30"
            />
            {dictation.error && (
              <p className="px-3 pb-1 text-[11px] text-red-400" data-testid="message-mic-error">
                {dictation.error}
              </p>
            )}
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between px-3 pb-2">
              <div className="flex flex-wrap items-center gap-3">
                <label data-testid="attach-file-trigger" className="text-fg/40 hover:text-fg cursor-pointer flex items-center gap-1 text-xs">
                  <Paperclip className="w-3.5 h-3.5" />
                  {uploading ? "se încarcă…" : "attach"}
                  <input data-testid="attach-file-input" type="file" multiple onChange={onPick} className="hidden" />
                </label>
                <button data-testid="insert-code-button" type="button" onClick={insertCodeFence} className="flex items-center gap-1 text-xs text-fg/40 hover:text-fg">
                  <Code2 className="w-3.5 h-3.5" /> cod
                </button>
                {dictation.supported && (
                  <button
                    data-testid="message-mic" type="button" onClick={dictation.toggle}
                    aria-pressed={dictation.recording} disabled={dictation.working}
                    title="Dictează (se transcrie pe serverul nostru)"
                    className={`flex items-center gap-1 text-xs disabled:opacity-40 ${
                      dictation.recording ? "text-brand animate-pulse" : "text-fg/40 hover:text-fg"}`}
                  >
                    {dictation.recording ? <Mic className="w-3.5 h-3.5" /> : <MicOff className="w-3.5 h-3.5" />}
                    {dictation.working ? "se transcrie…" : "dictează"}
                  </button>
                )}
                <Popover open={emojiOpen} onOpenChange={setEmojiOpen}>
                  <PopoverTrigger asChild>
                    <button data-testid="emoji-trigger" type="button" className="flex items-center gap-1 text-xs text-fg/40 hover:text-fg">
                      <Smile className="w-3.5 h-3.5" /> emoji
                    </button>
                  </PopoverTrigger>
                  <PopoverContent align="start" className="bg-surface border-line/10 p-0 w-auto rounded-none">
                    <EmojiPicker onSelect={insertEmoji} />
                  </PopoverContent>
                </Popover>
                <button data-testid="report-bug-button" type="button" onClick={() => setFeedback({ title: "" })} className="flex items-center gap-1 text-xs text-fg/40 hover:text-fg">
                  <Bug className="w-3.5 h-3.5" /> raportează un defect
                </button>
              </div>
              <div className="flex flex-wrap items-center justify-end gap-2">
                {isOpsChannel && (
                  <>
                    <div className="relative">
                      <button
                        type="button"
                        onClick={() => setRunMenuOpen((open) => !open)}
                        disabled={!!runningCommand}
                        className="text-xs px-2.5 py-1 border border-brand/40 text-brand inline-flex items-center gap-1.5 disabled:opacity-40"
                        data-testid="ops-run-button"
                      >
                        {runningCommand ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Terminal className="w-3.5 h-3.5" />}
                        {runningCommand || "Rulează"}
                      </button>
                      {runMenuOpen && (
                        <div className="absolute bottom-full right-0 mb-2 w-52 max-h-64 overflow-y-auto bg-surface border border-line/20 shadow-xl z-30" data-testid="ops-run-menu">
                          {opsCommands.map((command) => (
                            <button
                              key={command}
                              type="button"
                              onClick={() => runOpsCommand(command)}
                              className="w-full text-left px-3 py-2 text-xs mono hover:bg-fg/5"
                            >
                              {command}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => setClaudeOpen((open) => !open)}
                      className="text-xs px-2.5 py-1 border border-brand/40 text-brand inline-flex items-center gap-1.5"
                      data-testid="ops-claude-button"
                    >
                      <Bot className="w-3.5 h-3.5" /> Claude
                    </button>
                    <button
                      type="button"
                      data-testid="ops-cc-contabo"
                      onClick={() => setCcServer(ccServer === "contabo" ? null : "contabo")}
                      className={`text-xs px-2.5 py-1 border inline-flex items-center gap-1.5 transition-colors ${
                        ccServer === "contabo" ? "border-brand/40 text-brand bg-brand/10" : "border-line/20 text-fg/60 hover:text-fg"}`}
                    >
                      <Terminal className="w-3.5 h-3.5" /> Contabo
                    </button>
                    <button
                      type="button"
                      data-testid="ops-cc-hetzner"
                      onClick={() => setCcServer(ccServer === "hetzner" ? null : "hetzner")}
                      className={`text-xs px-2.5 py-1 border inline-flex items-center gap-1.5 transition-colors ${
                        ccServer === "hetzner" ? "border-brand/40 text-brand bg-brand/10" : "border-line/20 text-fg/60 hover:text-fg"}`}
                    >
                      <Cloud className="w-3.5 h-3.5" /> Hetzner
                    </button>
                  </>
                )}
                <button data-testid="send-message-button" type="submit" disabled={sending || (!text.trim() && pendingFiles.length === 0)} className="btn-primary text-sm flex items-center gap-1.5 px-3 py-1">
                  Trimite <Send className="w-3 h-3" />
                </button>
              </div>
            </div>
          </div>
          <p className="label-tiny mt-2 opacity-50">Enter = trimite · Shift+Enter = rând nou · ``` pentru cod · @ pentru mențiuni</p>
        </form>
      </div>

      {threadParent && (
        <ThreadPanel
          parent={threadParent}
          workspaceId={workspaceId}
          currentUser={currentUser}
          subscribe={subscribe}
          onClose={() => setThreadParent(null)}
          onReact={react}
          onEdit={editMsg}
          onDelete={deleteMsg}
        />
      )}

      {feedback && (
        <FeedbackModal
          workspaceId={workspaceId}
          channelId={mode === "channel" ? targetId : null}
          initialTitle={feedback.title}
          onClose={() => setFeedback(null)}
        />
      )}
      <Dialog open={accessOpen} onOpenChange={setAccessOpen}>
        <DialogContent className="bg-surface border-line/10 text-fg rounded-none" data-testid="channel-access-dialog"
          description="Alege cine poate vedea și scrie în acest canal.">
          <DialogHeader>
            <DialogTitle className="font-bold tracking-tight">Acces la #{target.name}</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-fg-muted leading-relaxed">
            Cine poate citi și scrie în acest canal. Proprietarul și administratorii rămân mereu bifați —
            altfel canalul ar deveni imposibil de administrat.
          </p>
          <div className="space-y-2">
            {ROLES.map((r) => {
              const locked = r === "owner" || r === "admin";
              const checked = locked || accessRoles.includes(r);
              return (
                <label key={r} className={`flex items-center gap-2.5 text-sm ${locked ? "opacity-60" : "cursor-pointer"}`}>
                  <input
                    type="checkbox"
                    data-testid={`channel-access-${r}`}
                    disabled={locked}
                    checked={checked}
                    onChange={(e) => setAccessRoles((prev) =>
                      e.target.checked ? [...new Set([...prev, r])] : prev.filter((x) => x !== r))}
                  />
                  <span className="uppercase mono text-xs tracking-wider">{roleLabel(r)}</span>
                </label>
              );
            })}
          </div>
          <DialogFooter>
            <button type="button" onClick={() => setAccessOpen(false)} className="btn-ghost text-sm">Anulează</button>
            <button type="button" data-testid="channel-access-save" onClick={saveAccess} disabled={accessSaving} className="btn-primary text-sm disabled:opacity-50">
              {accessSaving ? "Se salvează…" : "Salvează"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <ConfirmDialog {...confirmProps} />
    </div>
  );
}
